import { useState } from "react"
import {
	IconSquareArrowUpRightOutline18 as ExternalIcon,
	IconFileOutline18 as FileIcon,
	IconNodesOutline18 as Nodes,
	IconShieldCheckOutline18 as ShieldIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Badge, Button, Dialog, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import {
	SETTINGS_CARD_NOTICE_CLASS,
	SettingsContainer,
} from "../settings-switches/settings-container"

export function BugInspectPage() {
	const workspace = useZeroWorkspace()
	if (!workspace) return null

	return (
		<>
			<MetaInfo title="Bug Inspect Settings" />

			<SettingsHeader
				title="Bug Inspect"
				subtitle="Check bug reports against your code and draft a fix."
			/>

			<BugInspectSettingItem
				hasRepo={!!workspace.githubSettings?.repositoryFullName}
			/>
		</>
	)
}

// The agent that reads the repository to judge a customer's bug report. It
// answers with a status and nothing else, so the report never reaches the code
// and the code never reaches the customer.
function BugInspectSettingItem({ hasRepo }: { hasRepo: boolean }) {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const settings = workspace?.aiChatSettings
	const enabled = settings?.bugInspectEnabled ?? false

	const writeStatus = api.repoIntegration.getWriteAppStatus.useQuery(
		undefined,
		{ staleTime: 60_000, enabled: hasRepo },
	).data
	const launched = writeStatus?.available === true
	const ciQuery = api.bugInspect.ciStatus.useQuery(undefined, {
		staleTime: 60_000,
		enabled: hasRepo && launched,
	})

	if (!settings) return null

	const notice =
		ciQuery.data?.state === "unsupported" ? (
			<div
				className={cn(
					SETTINGS_CARD_NOTICE_CLASS,
					"flex items-center gap-2 bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-300",
				)}
			>
				This repo uses pull_request_target, so Bug Inspect can&apos;t run here.
			</div>
		) : writeStatus?.available &&
		  writeStatus.connected &&
		  writeStatus.coversLinkedRepository === false &&
		  writeStatus.linkedRepository ? (
			<div
				className={cn(
					SETTINGS_CARD_NOTICE_CLASS,
					"flex items-center gap-2 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200",
				)}
			>
				<span className="min-w-0 flex-1">
					Write access doesn&apos;t include {writeStatus.linkedRepository} yet.
				</span>
				<Button
					variant="outline"
					className="h-7 min-h-7 flex-none"
					onClick={() => {
						analytics.track("github-write-add-repo-clicked")
						window.location.href = writeStatus.installUrl
					}}
				>
					Add repository
				</Button>
			</div>
		) : hasRepo &&
		  enabled &&
		  writeStatus?.available &&
		  !writeStatus.connected ? (
			<div
				className={cn(
					SETTINGS_CARD_NOTICE_CLASS,
					"flex items-center gap-2 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200",
				)}
			>
				<span className="min-w-0 flex-1">
					Bug Inspect needs write access to run.
				</span>
				<Button
					variant="outline"
					className="h-7 min-h-7 flex-none"
					onClick={() => {
						analytics.track("github-write-install-clicked")
						window.location.href = writeStatus.installUrl
					}}
				>
					Add write access
				</Button>
			</div>
		) : null

	return (
		<>
			<SettingsContainer notice={notice}>
				<SettingItem
					title="Check bug reports against the code"
					description={
						hasRepo
							? "The support agent asks an engineering agent whether a reported bug is real, then answers or escalates."
							: "Add a repository to check bug reports."
					}
					action={
						// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
						<div
							onClick={() => {
								if (!hasRepo) {
									toast.info({ title: "Add a repository first", icon: Nodes })
								}
							}}
						>
							<Switch
								name="bugInspectEnabled"
								checked={hasRepo && enabled}
								disabled={!hasRepo}
								className={cn(!hasRepo && "pointer-events-none")}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.aiChatSettings.update({
												id: settings.id,
												bugInspectEnabled: value,
											}),
										),
									)
									analytics.track("update-bug-inspect-enabled")
								}}
							/>
						</div>
					}
				/>
				{hasRepo && <WriteAccessSettingItem />}
				{hasRepo && launched ? <WorkflowSecretsSettingItem /> : null}
			</SettingsContainer>
		</>
	)
}

export function workflowSecretsDescription(
	state: "needs-ack" | "changed" | "acknowledged" | "clear",
	count: number,
): string {
	if (state === "clear") return "No workflow can pass secrets to tests."
	if (state === "changed") return "A workflow changed since your review."
	return count === 1
		? "1 workflow can pass secrets to tests."
		: `${count} workflows can pass secrets to tests.`
}

// Runs stay paused while a workflow could hand secrets to Bug Inspect's test
// code, until an admin reviews the list. Editing a workflow asks again.
function WorkflowSecretsSettingItem() {
	const utils = api.useUtils()
	const [open, setOpen] = useState(false)
	const ciQuery = api.bugInspect.ciStatus.useQuery(undefined, {
		staleTime: 60_000,
	})
	const acknowledge = api.bugInspect.acknowledgeCi.useMutation({
		onSuccess: (status) => {
			utils.bugInspect.ciStatus.setData(undefined, status)
			setOpen(false)
		},
		onError: (error) => toast.info({ title: error.message, icon: ShieldIcon }),
	})

	const status = ciQuery.data
	if (
		!status ||
		status.state === "unavailable" ||
		status.state === "unsupported"
	) {
		return null
	}

	const needsReview = status.state === "needs-ack" || status.state === "changed"

	return (
		<>
			<SettingItem
				className="pt-4"
				title="Workflow secrets"
				description={workflowSecretsDescription(
					status.state,
					status.risky.length,
				)}
				action={
					<div className="flex items-center gap-2">
						{needsReview ? (
							<Badge variant="amber" size="sm">
								<span className="size-1.5 flex-none rounded-full bg-amber-500" />
								{status.state === "changed" ? "Review again" : "Runs paused"}
							</Badge>
						) : (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 flex-none rounded-full bg-green-500" />
								{status.state === "acknowledged" ? "Acknowledged" : "Clear"}
							</Badge>
						)}
						{needsReview ? (
							<Button
								variant="outline"
								className="h-8 min-h-8 rounded-md text-sm ring-inset dark:bg-gray-700/70 dark:text-gray-50 dark:shadow-none"
								onClick={() => setOpen(true)}
							>
								Review
							</Button>
						) : null}
					</div>
				}
			/>
			<Dialog
				isOpen={open}
				onOpenChange={setOpen}
				title="Review workflow secrets"
				footer={
					<div className="flex w-full items-center justify-end gap-2">
						<Button variant="outline" onClick={() => setOpen(false)}>
							Cancel
						</Button>
						<Button
							isLoading={acknowledge.isLoading}
							onClick={() => acknowledge.mutate()}
						>
							Acknowledge
						</Button>
					</div>
				}
			>
				<div className="grid gap-3 text-sm">
					<p className="text-secondary">
						These workflows can pass secrets to test code that Bug Inspect
						writes.
					</p>
					<ul className="grid gap-2">
						{status.risky.map((workflow) => (
							<li
								key={workflow.path}
								className="flex min-w-0 items-center gap-2"
							>
								<FileIcon className="h-4 w-4 flex-none text-tertiary" />
								<span className="min-w-0 truncate font-mono text-xs">
									{workflow.path}
								</span>
								<span className="flex flex-wrap gap-1">
									{workflow.secrets.map((secret) => (
										<Badge key={secret} variant="light-gray" size="sm">
											{secret}
										</Badge>
									))}
								</span>
							</li>
						))}
					</ul>
					<p className="text-tertiary">
						Exclude productlane/* branches in them, or acknowledge to run
						anyway. A workflow change asks again.
					</p>
				</div>
			</Dialog>
		</>
	)
}

// The server only returns it for workspaces where Bug Inspect is launched.
function WriteAccessSettingItem() {
	const statusQuery = api.repoIntegration.getWriteAppStatus.useQuery(
		undefined,
		{ staleTime: 60_000 },
	)
	const status = statusQuery.data

	if (!status?.available) return null

	return (
		<SettingItem
			className="pt-4"
			title="Write access"
			description={
				status.connected && status.accountLogin
					? `Installed on ${status.accountLogin}.`
					: "Opens fixes as draft pull requests."
			}
			action={
				status.connected ? (
					<Badge
						variant="green"
						size="sm"
						className="!text-green-700 dark:!text-green-400"
					>
						<span className="size-1.5 rounded-full bg-green-500" />
						Connected
					</Badge>
				) : (
					<Button
						variant="outline"
						iconRight={ExternalIcon}
						className="h-8 min-h-8 rounded-md text-sm ring-inset dark:bg-gray-700/70 dark:text-gray-50 dark:shadow-none"
						onClick={() => {
							analytics.track("github-write-install-clicked")
							window.location.href = status.installUrl
						}}
					>
						Install App
					</Button>
				)
			}
		/>
	)
}
